/**
 * Cash flow breakdown drawer — opened from "View full breakdown" on the cash
 * flow panel. Mirrors the breakdown drawer in `design/Syntra/property-detail.html`:
 * three summary stats, a "Where the income goes" allocation bar, and the full
 * itemized line items (with delete controls for logged expenses).
 */
import { Trash2 } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/calculators";
import type { RehabSpendItem } from "@/components/properties/cashflow-metrics";
import {
  EXPENSE_CATEGORIES,
  type YearMonthWithLogged,
} from "@/components/properties/cashflow-detail";
import type { Strategy } from "@/types/pipeline";

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  EXPENSE_CATEGORIES.map((c) => [c.value, c.label]),
);

interface CashflowBreakdownDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  month: YearMonthWithLogged | null;
  /** Rehab line items completed in this month (label + spend), listed by name. */
  rehabItems?: RehabSpendItem[];
  /** Deal strategy — a Flip swaps operating expenses for holding costs. */
  strategy?: Strategy;
  onDeleteExpense: (id: string) => void;
  isDeleting: boolean;
}

const PHASE_LABEL: Record<string, string> = {
  rehab: "rehab phase",
  hold: "holding phase",
  operating: "operating",
};

export function CashflowBreakdownDrawer({
  open,
  onOpenChange,
  month,
  rehabItems = [],
  strategy,
  onDeleteExpense,
  isDeleting,
}: CashflowBreakdownDrawerProps) {
  const b = month?.breakdown ?? null;
  // A Flip is carried, not rented: show holding costs (taxes+ins · utilities)
  // instead of the rental operating-expense reserves, and no rental income. The
  // holding is already baked into the breakdown's opex, sourced per-month (live
  // for the current month, frozen property_log for past months).
  const isFlip = strategy === "Flip";
  const holding = isFlip ? (b?.opex.total ?? 0) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{isFlip ? "Carrying-cost breakdown" : "Cash flow breakdown"}</SheetTitle>
          <SheetDescription>
            {month
              ? `${month.monthName} ${month.year} · ${PHASE_LABEL[month.phase] ?? "operating"}`
              : ""}
          </SheetDescription>
        </SheetHeader>

        {month && b && (
          <div className="mt-5 space-y-6">
            <Summary month={month} isFlip={isFlip} />
            <LineItems
              month={month}
              rehabItems={rehabItems}
              isFlip={isFlip}
              holding={holding}
              onDeleteExpense={onDeleteExpense}
              isDeleting={isDeleting}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ── Summary stats + allocation bar ───────────────────────────────────────── */
function Summary({
  month,
  isFlip,
}: {
  month: YearMonthWithLogged;
  isFlip: boolean;
}) {
  const b = month.breakdown!;
  const egi = b.income.egi;
  // For a flip, opex.total IS the holding (taxes+ins · utilities); for a rental
  // it's the operating reserves. Either way the net already reflects it.
  const opex = b.opex.total;
  const debt = b.debt;
  const rehab = month.rehabSpend;
  const logged = month.loggedTotal;
  const outflow = opex + debt + rehab + logged;
  const net = month.netWithLogged;
  const total = Math.max(egi, outflow, 1);
  const cashW = net > 0 ? net : 0;
  const pctOf = (v: number) => {
    const base = isFlip ? outflow : egi;
    return base > 0 ? `${Math.round((v / base) * 100)}%` : fmtMoney(v);
  };

  // Palette mirrors the "Where the money goes" section in the reference design
  // (design/Syntra/property-detail.html): debt = violet, operating = blue,
  // rehab = red, plus amber for logged extras and green for retained cash flow.
  const segments = [
    { key: "opex", label: isFlip ? "Holding" : "Operating", amount: opex, dot: "bg-blue-500" },
    { key: "debt", label: "Debt", amount: debt, dot: "bg-violet-500" },
    { key: "rehab", label: "Rehab", amount: rehab, dot: "bg-red-500" },
    { key: "logged", label: "Logged", amount: logged, dot: "bg-amber-500" },
    ...(isFlip ? [] : [{ key: "cash", label: "Cash flow", amount: cashW, dot: "bg-success" }]),
  ].filter((s) => s.amount > 0);

  return (
    <div className="space-y-4">
      <div className={cn("grid gap-2", isFlip ? "grid-cols-2" : "grid-cols-3")}>
        {!isFlip && <Stat label="Income" value={fmtMoney(egi)} tone="success" />}
        <Stat label={isFlip ? "Carry this month" : "Expenses"} value={fmtMoney(outflow)} tone="destructive" />
        <Stat label="Net" value={fmtMoney(net)} tone={net >= 0 ? "success" : "destructive"} emphasize />
      </div>
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Where the money goes
        </div>
        <div className="flex h-3 overflow-hidden rounded-full border border-border bg-muted">
          {segments.map((s) => (
            <div key={s.key} className={cn("h-full", s.dot)} style={{ width: `${(s.amount / total) * 100}%` }} />
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
          {segments.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-sm", s.dot)} />
              {s.label} <b className="font-semibold tabular-nums text-foreground">{pctOf(s.amount)}</b>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
  emphasize,
}: {
  label: string;
  value: string;
  tone: "success" | "destructive";
  emphasize?: boolean;
}) {
  return (
    <div className={cn("rounded-lg border border-border bg-muted/40 p-3", emphasize && "border-primary/20 bg-primary/5")}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn("mt-1 text-base font-bold tabular-nums", tone === "success" ? "text-success" : "text-destructive")}>
        {value}
      </div>
    </div>
  );
}

/* ── Full itemized line items ─────────────────────────────────────────────── */
function LineItems({
  month,
  rehabItems,
  isFlip,
  holding,
  onDeleteExpense,
  isDeleting,
}: {
  month: YearMonthWithLogged;
  rehabItems: RehabSpendItem[];
  isFlip: boolean;
  holding: number;
  onDeleteExpense: (id: string) => void;
  isDeleting: boolean;
}) {
  const b = month.breakdown!;
  const isRehab = month.phase === "rehab";
  const gross = b.income.gross;
  const pctOf = (v: number) => (gross > 0 ? `${Math.round((v / gross) * 100)}% rent` : "");
  // Per-month flip holding lines come straight from the breakdown's opex, so a
  // past month reflects its frozen snapshot rather than current settings.
  const flipTaxes = b.opex.lines.find((l) => l.key === "taxes")?.amount ?? 0;
  const flipUtilities = b.opex.lines.find((l) => l.key === "utilities")?.amount ?? 0;
  const net = month.netWithLogged;

  return (
    <div className="rounded-lg border border-border">
      {/* A flip earns no rental income — show holding costs instead. */}
      {isFlip ? (
        <>
          <Section label="Holding costs" />
          <Row item="Property taxes & insurance" amount={`-${fmtMoney(flipTaxes)}`} tone="destructive" />
          <Row item="Utilities" amount={`-${fmtMoney(flipUtilities)}`} tone="destructive" />
          <Row item="Total holding costs" amount={`-${fmtMoney(holding)}`} tone="destructive" subtotal />
        </>
      ) : (
        <>
          {!isRehab && (
            <>
              <Section label="Income" />
              <Row item="Gross rent" amount={`+${fmtMoney(b.income.gross)}`} tone="success" />
              <Row item="Other income" amount={b.income.other > 0 ? `+${fmtMoney(b.income.other)}` : fmtMoney(0)} tone={b.income.other > 0 ? "success" : undefined} />
              <Row item="Effective gross income" amount={`+${fmtMoney(b.income.egi)}`} tone="success" subtotal />
            </>
          )}

          <Section label={isRehab ? "Holding costs" : "Operating expenses"} />
          {b.opex.lines.map((l) => (
            <Row key={l.key} item={l.label} detail={pctOf(l.amount)} amount={`-${fmtMoney(l.amount)}`} tone="destructive" />
          ))}
          <Row item={isRehab ? "Total holding costs" : "Total operating expenses"} amount={`-${fmtMoney(b.opex.total)}`} tone="destructive" subtotal />
        </>
      )}

      {month.rehabSpend > 0 && (
        <>
          <Section label="Rehab spend" />
          {rehabItems.map((it, i) => (
            <Row
              key={`${it.label}-${i}`}
              item={it.label}
              amount={`-${fmtMoney(it.amount)}`}
              tone="warning"
            />
          ))}
          <Row item="Total rehab" amount={`-${fmtMoney(month.rehabSpend)}`} tone="warning" subtotal />
        </>
      )}

      <Section label="Debt service" />
      <Row item={isRehab || isFlip ? "Loan payment" : "Monthly loan payment"} amount={`-${fmtMoney(b.debt)}`} tone="destructive" />

      {month.logged.length > 0 && (
        <>
          <Section label="Logged expenses" />
          {month.logged.map((e) => (
            <div key={e.id} className="flex items-center gap-2 border-b border-border px-4 py-2.5 text-sm last:border-b-0">
              <span className="flex-1 text-foreground">
                {CATEGORY_LABEL[e.category] ?? e.category}
                {e.description && <span className="text-muted-foreground"> · {e.description}</span>}
              </span>
              <button
                type="button"
                onClick={() => onDeleteExpense(e.id)}
                disabled={isDeleting}
                aria-label="Remove expense"
                className="text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <span className="font-semibold tabular-nums text-destructive">-{fmtMoney(Number(e.amount))}</span>
            </div>
          ))}
          <Row item="Total logged" amount={`-${fmtMoney(month.loggedTotal)}`} tone="destructive" subtotal />
        </>
      )}

      <Row
        item={isFlip ? "Net carry this month" : "Net cash flow"}
        amount={`${net >= 0 ? "+" : ""}${fmtMoney(net)}`}
        tone={net >= 0 ? "success" : "destructive"}
        total
      />
    </div>
  );
}

function Section({ label }: { label: string }) {
  return (
    <div className="bg-muted/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
  );
}

function Row({
  item,
  detail,
  amount,
  tone,
  subtotal,
  total,
}: {
  item: string;
  detail?: string;
  amount: string;
  tone?: "success" | "destructive" | "warning";
  subtotal?: boolean;
  total?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-border px-4 py-2.5 text-sm last:border-b-0",
        subtotal && "bg-muted/30 font-medium",
        total && "border-t-2 border-border bg-muted/60 font-semibold",
      )}
    >
      <span className="text-foreground">{item}</span>
      <span className="text-right text-xs text-muted-foreground">{detail ?? ""}</span>
      <span
        className={cn(
          "text-right font-semibold tabular-nums",
          tone === "success" && "text-success",
          tone === "destructive" && "text-destructive",
          tone === "warning" && "text-warning",
        )}
      >
        {amount}
      </span>
    </div>
  );
}
